// v14l — Emma-wizard voor eenvoudige e-mailkoppeling.
// Doel: gebruiker hoeft SMTP/poort/TLS niet te kennen. Emma vult die zelf in
// voor bekende providers (Gmail, Outlook, iCloud) en legt in gewone taal uit
// hoe je een app-password aanmaakt.
//
// Bestaande handmatige SMTP-config blijft beschikbaar onder "Geavanceerde
// instellingen" — deze wizard vervangt niets, hij is een primaire vereenvoudiging.
//
// Security-belofte: het wachtwoord/app-password wordt versleuteld opgeslagen
// (Fernet op server) en is na opslaan niet opnieuw zichtbaar. Deze zin toont
// de wizard expliciet aan de gebruiker (per gebruikersverzoek).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Sparkles, ArrowLeft, ArrowRight, Check, Info, ShieldCheck,
  ExternalLink, X, Globe,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ────────────────────────────────────────────────────────────────
// Provider-presets
// Elke preset bevat: technische SMTP-config + copy voor de gebruiker.
// Voor "andere" laten we de wizard geen host invullen — de gebruiker
// gaat door naar het geavanceerde formulier via "Geavanceerde instellingen".
// ────────────────────────────────────────────────────────────────
const PROVIDERS = [
  {
    id: "gmail",
    label: "Gmail / Google Workspace",
    subtitle: "@gmail.com of eigen domein via Google",
    icon: "G",
    color: "#EA4335",
    smtp: { host: "smtp.gmail.com", port: 587, use_tls: true },
    passwordType: "app-password",
    intro:
      "Voor Gmail gebruik je in bijna alle gevallen een 'app-wachtwoord' — dat is een aparte code van 16 letters die je zelf aanmaakt bij Google. Je gewone Gmail-wachtwoord werkt niet.",
    steps: [
      "Zorg dat 2-staps-verificatie ingeschakeld is op je Google-account (via myaccount.google.com/security).",
      "Ga naar Google → App-wachtwoorden (myaccount.google.com/apppasswords).",
      "Kies bij 'App' de optie 'Mail' en bij 'Apparaat' 'Andere' → typ 'EmmaMeasure'.",
      "Google toont een code van 16 letters (zonder spaties). Kopieer die code.",
      "Plak die code hieronder als 'App-wachtwoord'.",
    ],
    helpUrl: "https://myaccount.google.com/apppasswords",
    helpUrlLabel: "Open Google app-wachtwoorden",
    warning:
      "Als je organisatie Google Workspace gebruikt en app-wachtwoorden zijn uitgeschakeld door de beheerder, moet je de IT-verantwoordelijke vragen om ze toe te staan of om SMTP-verzending te configureren.",
  },
  {
    id: "outlook",
    label: "Outlook / Microsoft 365",
    subtitle: "@outlook.com, @hotmail.com of Microsoft 365",
    icon: "O",
    color: "#0078D4",
    smtp: { host: "smtp.office365.com", port: 587, use_tls: true },
    passwordType: "app-password-of-account",
    intro:
      "Voor Outlook of Microsoft 365 gebruik je ofwel een app-wachtwoord (als je 2-staps-verificatie hebt), ofwel — bij oudere accounts — je gewone Outlook-wachtwoord. Welke van de twee werkt hangt af van je account en het beleid van je organisatie.",
    steps: [
      "Heb je 2-staps-verificatie? Ga naar account.microsoft.com/security → 'Geavanceerde beveiligingsopties' → 'App-wachtwoorden' → maak een nieuw wachtwoord voor 'EmmaMeasure'.",
      "Heb je géén 2-staps-verificatie? Gebruik dan gewoon je Outlook-wachtwoord.",
      "Voor Microsoft 365 zakelijk kan het zijn dat je beheerder SMTP-verzending eerst moet inschakelen. Vraag dit na als de test verbinding blijft mislukken.",
      "Plak het wachtwoord (of app-wachtwoord) hieronder.",
    ],
    helpUrl: "https://account.microsoft.com/security",
    helpUrlLabel: "Open Microsoft-accountbeveiliging",
    warning:
      "Sommige zakelijke Microsoft 365-omgevingen blokkeren SMTP-verzending vanuit externe apps volledig. In dat geval is een technische ingreep door de IT-beheerder vereist — laat je hier niet raden.",
  },
  {
    id: "icloud",
    label: "iCloud / @me.com / @icloud.com",
    subtitle: "Apple Mail via iCloud",
    icon: "",
    color: "#000000",
    smtp: { host: "smtp.mail.me.com", port: 587, use_tls: true },
    passwordType: "app-password",
    intro:
      "Voor iCloud (@me.com of @icloud.com) heb je ALTIJD een 'app-specifiek wachtwoord' nodig. Je gewone Apple ID-wachtwoord werkt niet voor SMTP. Je maakt zo'n app-wachtwoord aan op de website van Apple.",
    steps: [
      "Zorg dat 2-factor-authenticatie actief is op je Apple ID (bij nieuwe iCloud-accounts standaard aan).",
      "Ga naar appleid.apple.com en meld je aan.",
      "Ga naar 'Aanmelden en beveiliging' → 'App-specifieke wachtwoorden' → '+ Genereer een app-specifiek wachtwoord'.",
      "Geef als label 'EmmaMeasure' en klik 'Maak aan'.",
      "Apple toont een wachtwoord in de vorm xxxx-xxxx-xxxx-xxxx. Kopieer het volledig (met de streepjes).",
      "Plak het als 'App-wachtwoord' hieronder.",
    ],
    helpUrl: "https://appleid.apple.com",
    helpUrlLabel: "Open Apple ID-instellingen",
    warning: null,
  },
  {
    id: "local_common",
    label: "Veelgebruikte provider",
    subtitle: "Proximus, Telenet, KPN, GMX, Orange, SFR…",
    icon: "🌍",
    color: "#5D7259",
    smtp: null,
    passwordType: "provider-specifiek",
    intro: null, // niet gebruikt — leidt naar substep "kies land"
    steps: [],
    helpUrl: null,
    warning: null,
    isCountryPicker: true, // v14n — trigger substep
  },
  {
    id: "other",
    label: "Andere e-mailprovider",
    subtitle: "Eigen domein, hosting, Combell, OVH, One.com…",
    icon: "•",
    color: "#6E6E6C",
    smtp: null,
    passwordType: "provider-specifiek",
    intro:
      "Voor andere providers heb je de technische SMTP-gegevens nodig van je e-mailprovider (host, poort, wachtwoord). Sluit deze wizard en vul die in bij 'Geavanceerde instellingen'.",
    steps: [
      "Vraag bij je e-mailprovider naar 'SMTP-uitgaande server': host, poort, TLS.",
      "Sluit deze wizard en klik op 'Geavanceerde instellingen' hieronder.",
      "Vul host, poort, gebruikersnaam en wachtwoord manueel in.",
    ],
    helpUrl: null,
    warning: null,
  },
];

// ────────────────────────────────────────────────────────────────
// v14n — Landspecifieke providerlijst
// Enkel providers met publiek gedocumenteerde SMTP-instellingen.
// Volgorde per land: op marktaandeel/herkenbaarheid.
// Elke provider heeft dezelfde vorm als PROVIDERS zodat Stap 2 volledig
// hergebruikt wordt.
// ────────────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: "BE", label: "België", flag: "🇧🇪" },
  { code: "NL", label: "Nederland", flag: "🇳🇱" },
  { code: "DE", label: "Duitsland", flag: "🇩🇪" },
  { code: "FR", label: "Frankrijk", flag: "🇫🇷" },
];

const COUNTRY_PROVIDERS = {
  BE: [
    {
      id: "proximus",
      label: "Proximus / Skynet",
      subtitle: "@skynet.be, @proximus.be",
      icon: "P",
      color: "#5B287D",
      smtp: { host: "relay.proximus.be", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Voor Proximus/Skynet gebruik je je gewone Skynet- of Proximus-wachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam (bv. jij@skynet.be).",
        "Gebruik je gewone Proximus/Skynet-wachtwoord.",
        "Werkt het niet? Controleer of je Skynet-account nog actief is via mijn.proximus.be.",
      ],
      helpUrl: "https://www.proximus.be/support/en/id_sfaqr_email_settings/",
      helpUrlLabel: "Open Proximus e-mail-help",
      warning: null,
    },
    {
      id: "telenet",
      label: "Telenet",
      subtitle: "@telenet.be",
      icon: "T",
      color: "#FFC800",
      smtp: { host: "uit.telenet.be", port: 587, use_tls: true },
      passwordType: "account",
      intro:
        "Voor Telenet gebruik je je gewone Telenet-wachtwoord. Verzending werkt het betrouwbaarst wanneer je op het Telenet-netwerk zit.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam (bv. jij@telenet.be).",
        "Gebruik je gewone Telenet-wachtwoord.",
        "Werkt de test niet? Log in op mijn.telenet.be en controleer of je e-mail-alias nog actief is.",
      ],
      helpUrl: "https://www2.telenet.be/nl/klantenservice/e-mail-instellingen/",
      helpUrlLabel: "Open Telenet e-mail-help",
      warning:
        "Telenet blokkeert soms uitgaande SMTP-verbindingen buiten het Telenet-netwerk. Werk je via een andere internetverbinding? Dan kan de test mislukken zelfs met correcte gegevens.",
    },
  ],
  NL: [
    {
      id: "kpn",
      label: "KPN Mail",
      subtitle: "@kpnmail.nl, @planet.nl, @hetnet.nl",
      icon: "K",
      color: "#008C15",
      smtp: { host: "smtp.kpnmail.nl", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Voor KPN Mail gebruik je je gewone KPN-wachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je gewone KPN-wachtwoord.",
        "Werkt het niet? Controleer via mijn.kpn.com of je e-mail-account nog actief is.",
      ],
      helpUrl: "https://www.kpn.com/service/internet/e-mail/",
      helpUrlLabel: "Open KPN e-mail-help",
      warning: null,
    },
    {
      id: "ziggo",
      label: "Ziggo",
      subtitle: "@ziggo.nl",
      icon: "Z",
      color: "#EE7203",
      smtp: { host: "smtp.ziggo.nl", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Voor Ziggo gebruik je je gewone Ziggo Mail-wachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam (bv. jij@ziggo.nl).",
        "Gebruik je gewone Ziggo Mail-wachtwoord.",
        "Werkt het niet? Log in op mijnziggo.nl en controleer of je Mail-account nog actief is.",
      ],
      helpUrl: "https://www.ziggo.nl/klantenservice/e-mail/",
      helpUrlLabel: "Open Ziggo e-mail-help",
      warning: null,
    },
  ],
  DE: [
    {
      id: "t_online",
      label: "Telekom / T-Online",
      subtitle: "@t-online.de",
      icon: "T",
      color: "#E20074",
      smtp: { host: "securesmtp.t-online.de", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Für T-Online gebruik je je gewone T-Online-wachtwoord of, indien ingesteld, je afzonderlijke e-mail-wachtwoord.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je T-Online e-mail-wachtwoord (kan afwijken van je Telekom-account-wachtwoord).",
        "Werkt het niet? Reset je e-mail-wachtwoord via email.t-online.de.",
      ],
      helpUrl: "https://www.telekom.de/hilfe/festnetz-internet-tv/e-mail",
      helpUrlLabel: "Open Telekom e-mail-help",
      warning: null,
    },
    {
      id: "gmx",
      label: "GMX",
      subtitle: "@gmx.de, @gmx.net",
      icon: "G",
      color: "#1C449B",
      smtp: { host: "mail.gmx.net", port: 587, use_tls: true },
      passwordType: "account",
      intro:
        "Voor GMX moet je in je GMX-account expliciet toestemming geven om via externe programma's e-mail te versturen (POP3/IMAP-toegang activeren).",
      steps: [
        "Log in op gmx.net → 'E-Mail' → 'Instellingen' → 'POP3/IMAP-toegang' → activeer.",
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je gewone GMX-wachtwoord.",
      ],
      helpUrl: "https://hilfe.gmx.net/pop-imap/einschalten.html",
      helpUrlLabel: "Open GMX POP/IMAP-instellingen",
      warning: null,
    },
    {
      id: "web_de",
      label: "WEB.DE",
      subtitle: "@web.de",
      icon: "W",
      color: "#FFCC00",
      smtp: { host: "smtp.web.de", port: 587, use_tls: true },
      passwordType: "account",
      intro:
        "Voor WEB.DE moet je in je account expliciet POP3/IMAP-toegang activeren voordat externe programma's mail mogen versturen.",
      steps: [
        "Log in op web.de → 'E-Mail' → 'Instellingen' → 'POP3/IMAP' → activeer.",
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je gewone WEB.DE-wachtwoord.",
      ],
      helpUrl: "https://hilfe.web.de/pop-imap/einschalten.html",
      helpUrlLabel: "Open WEB.DE POP/IMAP-instellingen",
      warning: null,
    },
  ],
  FR: [
    {
      id: "orange_fr",
      label: "Orange",
      subtitle: "@orange.fr, @wanadoo.fr",
      icon: "O",
      color: "#FF6600",
      smtp: { host: "smtp.orange.fr", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Pour Orange, gebruik je je gewone Orange e-mail-wachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je Orange e-mail-wachtwoord.",
        "Werkt het niet? Controleer via assistance.orange.fr of je account nog actief is.",
      ],
      helpUrl: "https://assistance.orange.fr/mail-internet-fixe/",
      helpUrlLabel: "Open Orange e-mail-help",
      warning: null,
    },
    {
      id: "sfr",
      label: "SFR",
      subtitle: "@sfr.fr, @neuf.fr",
      icon: "S",
      color: "#E4002B",
      smtp: { host: "smtp.sfr.fr", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Voor SFR gebruik je je gewone SFR Mail-wachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam.",
        "Gebruik je SFR Mail-wachtwoord.",
        "Werkt het niet? Reset je wachtwoord via www.sfr.fr/mon-espace-client.",
      ],
      helpUrl: "https://assistance.sfr.fr/internet-tel-fixe/mail-sfr/",
      helpUrlLabel: "Open SFR e-mail-help",
      warning: null,
    },
    {
      id: "free_fr",
      label: "Free",
      subtitle: "@free.fr",
      icon: "F",
      color: "#CD1719",
      smtp: { host: "smtp.free.fr", port: 465, use_tls: true },
      passwordType: "account",
      intro:
        "Voor Free gebruik je je gewone Free-e-mailwachtwoord. Emma vult de technische gegevens zelf in.",
      steps: [
        "Gebruik je volledige e-mailadres als gebruikersnaam (bv. jij@free.fr).",
        "Gebruik je gewone Free-wachtwoord.",
        "Werkt het niet? Log in op subscribe.free.fr en controleer of je e-mail-account actief is.",
      ],
      helpUrl: "https://assistance.free.fr/",
      helpUrlLabel: "Open Free helpcentrum",
      warning: null,
    },
  ],
};

// ────────────────────────────────────────────────────────────────
// Vertaal een technische error-string naar een korte begrijpelijke zin.
// We tonen NOOIT ruwe SMTP-foutcodes of stack traces aan de gebruiker.
// ────────────────────────────────────────────────────────────────
function humanizeSmtpError(msg) {
  const m = (msg || "").toString().toLowerCase();
  if (!m) return "Verbinding mislukt — controleer je gegevens en probeer opnieuw.";
  if (m.includes("authentication") || m.includes("auth") || m.includes("535") || m.includes("password"))
    return "Aanmelden mislukt. Controleer je wachtwoord of maak een app-specifiek wachtwoord aan.";
  if (m.includes("timeout") || m.includes("timed out"))
    return "Server reageerde niet op tijd. Controleer je internetverbinding of probeer opnieuw.";
  if (m.includes("connection refused") || m.includes("connect"))
    return "Kan geen verbinding maken met de server. Controleer host en poort.";
  if (m.includes("ssl") || m.includes("tls") || m.includes("certificate"))
    return "Beveiligde verbinding mislukt. Controleer of TLS aan staat.";
  if (m.includes("relay") || m.includes("not permitted"))
    return "De server weigert je bericht door te sturen. Vraag je beheerder om SMTP-verzending in te schakelen.";
  return "Verbinding mislukt. Controleer je e-mailadres en wachtwoord en probeer opnieuw.";
}

// ────────────────────────────────────────────────────────────────
// Kleine hulp: infer default from_email en user-veld op basis van e-mailadres
// ────────────────────────────────────────────────────────────────
function autoFill(email, provider) {
  const cleaned = (email || "").trim().toLowerCase();
  return {
    host: provider?.smtp?.host || "",
    port: provider?.smtp?.port || 587,
    use_tls: provider?.smtp?.use_tls ?? true,
    user: cleaned,
    from_email: cleaned,
  };
}

// ────────────────────────────────────────────────────────────────
// EmailWizard component
// ────────────────────────────────────────────────────────────────
export default function EmailWizard({ open, onOpenChange, onSaved }) {
  const [step, setStep] = useState(1);
  // v14n — subview binnen Stap 1: "main" | "country" | "country-providers"
  const [view, setView] = useState("main");
  const [country, setCountry] = useState("BE"); // default; wordt overschreven
  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fromName, setFromName] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // v14n — country auto-detect op basis van CompanyProfile
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/company`, { credentials: "include" });
        if (!r.ok) return;
        const c = await r.json();
        const cc = (c?.country || "").toUpperCase();
        if (!cancelled && COUNTRY_PROVIDERS[cc]) setCountry(cc);
      } catch (_) { /* stille fallback op default BE */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const reset = () => {
    setStep(1); setView("main"); setProvider(null);
    setEmail(""); setPassword("");
    setFromName(""); setSaving(false); setTesting(false); setTestResult(null);
  };
  const close = () => { reset(); onOpenChange(false); };

  const pickProvider = (p) => {
    if (p.isCountryPicker) {
      setView("country");
      return;
    }
    setProvider(p);
    if (p.id === "other") {
      // Andere provider → sluit wizard en verwijs naar geavanceerd
      onOpenChange(false);
      toast.info("Open 'Geavanceerde instellingen' hieronder om je SMTP-gegevens handmatig in te vullen.");
      reset();
      return;
    }
    setStep(2);
  };

  const pickCountry = (code) => {
    setCountry(code);
    setView("country-providers");
  };

  const canContinue = provider && email.trim() && password.trim();

  const saveAndTest = async () => {
    if (!canContinue) return;
    setSaving(true); setTestResult(null);
    try {
      const auto = autoFill(email, provider);
      const body = {
        smtp_config: {
          host: auto.host, port: auto.port, use_tls: auto.use_tls,
          user: auto.user, password,
          from_name: fromName || "", from_email: auto.from_email,
        },
      };
      const r = await fetch(`${API}/auth/me/smtp-config`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(`save ${r.status}`);
      // Test verbinding
      setTesting(true);
      const t = await fetch(`${API}/auth/me/smtp-test`, {
        method: "POST", credentials: "include",
      });
      const td = await t.json();
      setTestResult({ ok: !!td.ok, message: td.ok ? "Verbinding gelukt" : humanizeSmtpError(td.message) });
      if (td.ok) {
        toast.success("Je mailbox is gekoppeld");
        setStep(3);
        onSaved && onSaved();
      } else {
        toast.error(humanizeSmtpError(td.message));
      }
    } catch (e) {
      setTestResult({ ok: false, message: "Opslaan mislukt. Probeer opnieuw." });
      toast.error("Opslaan mislukt");
    } finally { setSaving(false); setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="email-wizard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#B07A4F]" />
            Emma helpt je e-mail koppelen
          </DialogTitle>
          <DialogDescription>
            {step === 1 && view === "main" && "Welke mailbox gebruik je? Emma vult daarna alle technische gegevens voor je in."}
            {step === 1 && view === "country" && "Voor welk land wil je een provider kiezen?"}
            {step === 1 && view === "country-providers" && `Kies je provider in ${COUNTRIES.find(c => c.code === country)?.label || country}.`}
            {step === 2 && provider && `Je gebruikt ${provider.label}. Ik vul de technische gegevens al in — jij vult alleen nog je adres en wachtwoord in.`}
            {step === 3 && "Klaar! Je mailbox is gekoppeld."}
          </DialogDescription>
        </DialogHeader>

        {/* Stap 1 — provider kiezen (main view) */}
        {step === 1 && view === "main" && (
          <div className="space-y-2" data-testid="wizard-step-1">
            {PROVIDERS.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickProvider(p)}
                className="w-full flex items-center gap-3 p-3 border border-[#E5E2DC] rounded-xl hover:bg-[#FAF9F6] text-left transition"
                data-testid={`wizard-provider-${p.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: p.color }}
                >
                  {p.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2C2A]">{p.label}</div>
                  <div className="text-xs text-[#6E6E6C]">{p.subtitle}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#B07A4F] flex-shrink-0" />
              </button>
            ))}
            <p className="text-[11px] text-[#8B8B87] italic flex items-center gap-1 pt-2">
              <ShieldCheck className="w-3 h-3" />
              Je wachtwoord of app-wachtwoord wordt versleuteld opgeslagen en is na opslaan niet opnieuw zichtbaar.
            </p>
          </div>
        )}

        {/* v14n — Stap 1b: kies land */}
        {step === 1 && view === "country" && (
          <div className="space-y-2" data-testid="wizard-step-country">
            {COUNTRIES.map((c) => (
              <button
                key={c.code}
                type="button"
                onClick={() => pickCountry(c.code)}
                className="w-full flex items-center gap-3 p-3 border border-[#E5E2DC] rounded-xl hover:bg-[#FAF9F6] text-left transition"
                data-testid={`wizard-country-${c.code}`}
              >
                <div className="w-10 h-10 rounded-lg bg-[#F0EFEA] flex items-center justify-center text-2xl flex-shrink-0">
                  {c.flag}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2C2A]">{c.label}</div>
                  <div className="text-xs text-[#6E6E6C]">
                    {(COUNTRY_PROVIDERS[c.code] || []).map(p => p.label.split(" /")[0]).join(" · ")}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#B07A4F] flex-shrink-0" />
              </button>
            ))}
            <Button
              onClick={() => setView("main")}
              variant="outline"
              className="w-full rounded-xl h-10 mt-2"
              data-testid="wizard-country-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Terug
            </Button>
          </div>
        )}

        {/* v14n — Stap 1c: providerlijst per land */}
        {step === 1 && view === "country-providers" && (
          <div className="space-y-2" data-testid="wizard-step-country-providers">
            {(COUNTRY_PROVIDERS[country] || []).map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => pickProvider(p)}
                className="w-full flex items-center gap-3 p-3 border border-[#E5E2DC] rounded-xl hover:bg-[#FAF9F6] text-left transition"
                data-testid={`wizard-local-provider-${p.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: p.color }}
                >
                  {p.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#2C2C2A]">{p.label}</div>
                  <div className="text-xs text-[#6E6E6C]">{p.subtitle}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-[#B07A4F] flex-shrink-0" />
              </button>
            ))}
            <Button
              onClick={() => setView("country")}
              variant="outline"
              className="w-full rounded-xl h-10 mt-2"
              data-testid="wizard-local-providers-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Terug (ander land)
            </Button>
            <p className="text-[11px] text-[#8B8B87] italic flex items-center gap-1 pt-1">
              <Info className="w-3 h-3" />
              Staat jouw provider hier niet? Gebruik dan "Andere e-mailprovider" of de geavanceerde instellingen.
            </p>
          </div>
        )}

        {/* Stap 2 — provider-info + credentials */}
        {step === 2 && provider && (
          <div className="space-y-4" data-testid="wizard-step-2">
            {/* Provider-uitleg */}
            <div className="bg-[#FDF7F2] border border-[#F4D6C4] rounded-xl p-3 space-y-2">
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-[#B07A4F] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#2C2C2A] leading-relaxed">{provider.intro}</p>
              </div>
              <details className="text-xs" data-testid="wizard-password-help">
                <summary className="cursor-pointer text-[#B07A4F] font-semibold hover:underline">
                  Waar vind ik mijn wachtwoord? →
                </summary>
                <ol className="mt-2 list-decimal list-outside pl-5 space-y-1 text-[#2C2C2A]">
                  {provider.steps.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
                {provider.helpUrl && (
                  <a
                    href={provider.helpUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-[#B07A4F] font-semibold hover:underline"
                    data-testid="wizard-provider-help-link"
                  >
                    {provider.helpUrlLabel} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {provider.warning && (
                  <p className="mt-2 text-[11px] text-[#8B4A2A] italic">{provider.warning}</p>
                )}
              </details>
            </div>

            {/* Credentials */}
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Je e-mailadres</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={
                    provider.id === "gmail" ? "jij@gmail.com"
                    : provider.id === "outlook" ? "jij@outlook.com"
                    : provider.id === "icloud" ? "jij@icloud.com"
                    : "jij@domein.be"
                  }
                  data-testid="wizard-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">
                  {provider.passwordType === "app-password"
                    ? "App-specifiek wachtwoord"
                    : provider.passwordType === "account"
                    ? "Wachtwoord"
                    : "Wachtwoord of app-wachtwoord"}
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    provider.passwordType === "account"
                      ? "Je gewone wachtwoord bij deze provider"
                      : "Plak hier de code die je bij je provider hebt aangemaakt"
                  }
                  data-testid="wizard-password"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Afzendernaam <span className="text-[10px] text-[#8B8B87] font-normal">(optioneel — verschijnt bij ontvanger)</span></Label>
                <Input
                  value={fromName}
                  onChange={(e) => setFromName(e.target.value)}
                  placeholder="Bv. Yves — YD Creaties"
                  data-testid="wizard-from-name"
                />
              </div>
              <p className="text-[11px] text-[#8B8B87] italic flex items-center gap-1">
                <ShieldCheck className="w-3 h-3" />
                Je wachtwoord/app-wachtwoord wordt versleuteld opgeslagen en is na opslaan niet opnieuw zichtbaar.
              </p>
            </div>

            {testResult && (
              <div
                className={`text-xs rounded-lg border px-3 py-2 ${
                  testResult.ok
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
                data-testid="wizard-test-result"
              >
                {testResult.message}
              </div>
            )}

            <div className="flex flex-wrap gap-2 justify-between">
              <Button
                onClick={() => {
                  // v14n — terug naar juiste substep afhankelijk van bron
                  const isLocalCommon = provider &&
                    (COUNTRY_PROVIDERS[country] || []).some(x => x.id === provider.id);
                  setStep(1);
                  setView(isLocalCommon ? "country-providers" : "main");
                  setTestResult(null);
                }}
                variant="outline"
                className="rounded-xl h-10"
                data-testid="wizard-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Terug
              </Button>
              <Button
                onClick={saveAndTest}
                disabled={!canContinue || saving || testing}
                className="bg-[#B07A4F] hover:bg-[#8f6440] text-white rounded-xl h-10"
                data-testid="wizard-save-btn"
              >
                {saving || testing
                  ? (testing ? "Test verbinding…" : "Opslaan…")
                  : "Opslaan en verbinding testen"}
              </Button>
            </div>
          </div>
        )}

        {/* Stap 3 — success */}
        {step === 3 && (
          <div className="space-y-3 text-center py-4" data-testid="wizard-step-3">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-green-700" />
            </div>
            <p className="font-semibold text-[#2C2C2A]">Je mailbox is gekoppeld</p>
            <p className="text-sm text-[#4F4F4D]">
              Je kan nu offertes rechtstreeks vanuit EmmaMeasure verzenden, inclusief PDF-bijlage.
            </p>
            <Button
              onClick={close}
              className="bg-[#B07A4F] hover:bg-[#8f6440] text-white rounded-xl h-10 mt-2"
              data-testid="wizard-done-btn"
            >
              Sluiten
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
